import React, { useState } from "react";
import { X, Sparkles, BookOpen, ArrowRight, Check } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { CONSULT_GOALS } from "../lib/services";
import { submitConsult } from "../lib/api";
import { saveCustomerInfo } from "../lib/tracker";
import { toast } from "sonner";

const OPTIONS = CONSULT_GOALS;

const SUBMISSIONS_KEY = "authoros_consults";
export const CONSULT_SUBMITTED_KEY = "authoros_consult_submitted";

export function hasSubmittedConsult() {
  try {
    return localStorage.getItem(CONSULT_SUBMITTED_KEY) === "1";
  } catch (_e) {
    return false;
  }
}

function markConsultSubmitted() {
  try {
    localStorage.setItem(CONSULT_SUBMITTED_KEY, "1");
  } catch (_e) { /* ignore */ }
}

function saveSubmissionLocal(entry) {
  try {
    const raw = localStorage.getItem(SUBMISSIONS_KEY);
    const list = raw ? JSON.parse(raw) : [];
    list.unshift(entry);
    localStorage.setItem(SUBMISSIONS_KEY, JSON.stringify(list));
  } catch (_e) { /* ignore */ }
}

export default function ConsultModal({ open, onOpenChange }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    goal: "",
    brief: "",
    agree: false,
  });
  const [submitted, setSubmitted] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.brief || !form.agree) {
      toast.error("Please complete every field to send your brief.");
      return;
    }
    const localEntry = {
      id: "csl_" + Math.random().toString(36).slice(2, 10),
      createdAt: new Date().toISOString(),
      ...form,
    };
    try {
      await submitConsult({
        name: form.name,
        email: form.email,
        phone: form.phone,
        goal: form.goal || "",
        brief: form.brief,
        agree: form.agree,
      });
      saveCustomerInfo({ email: form.email, phone: form.phone, name: form.name });
    } catch (_err) {
      // Backend unavailable — fall back to local so at least the UX completes
      saveSubmissionLocal(localEntry);
      toast.message("Saved locally (network issue). We'll retry on next visit.");
    }
    markConsultSubmitted();
    setSubmitted(true);
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setSubmitted(false);
      setForm({ name: "", email: "", phone: "", goal: "", brief: "", agree: false });
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="consult-modal"
        onPointerDownOutside={close}
        className="left-0 top-10 grid h-[calc(100dvh-2.5rem)] w-screen max-w-none translate-x-0 translate-y-0 gap-0 overflow-hidden rounded-t-2xl border-brand-hair bg-brand-panel p-0 text-brand-ivory sm:left-[50%] sm:top-[50%] sm:h-auto sm:max-h-[calc(100dvh-32px)] sm:w-[calc(100vw-24px)] sm:max-w-[960px] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg"
      >
        <button
          onClick={close}
          data-testid="consult-modal-close"
          className="absolute right-3 top-3 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white/90 shadow-lg backdrop-blur-sm transition-colors hover:bg-black/80 sm:right-4 sm:top-4 sm:h-9 sm:w-9 sm:bg-black/40 sm:shadow-none"
          aria-label="Close"
        >
          <X className="h-5 w-5 sm:h-4 sm:w-4" />
        </button>

        <div className="grid h-full grid-cols-1 overflow-y-auto md:grid-cols-[380px_1fr] md:overflow-hidden">
          {/* Left orange panel */}
          <div className="relative flex flex-col justify-between overflow-hidden bg-brand p-6 text-white sm:p-8 md:justify-between">
            <div className="absolute -right-16 -top-16 h-52 w-52 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -left-10 bottom-0 h-40 w-40 rounded-full bg-black/10 blur-2xl" />

            <div className="relative">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-black/25 backdrop-blur-sm">
                <span className="font-serif-display text-[22px] italic leading-none">A</span>
              </div>
              <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.22em] sm:mt-8">
                <Sparkles className="h-3 w-3" /> Free Consult
              </div>
              <h3 className="mt-4 font-serif-display text-[26px] font-semibold leading-[1.05] tracking-tight sm:mt-6 sm:text-[34px]">
                Not sure where to start with your book?
              </h3>
              <p className="mt-3 max-w-[280px] text-[13px] leading-relaxed text-white/85 sm:mt-4 sm:text-[13.5px]">
                Share a few details — our editors will send you a customised plan for publishing, marketing, or whatever you need next.
              </p>
            </div>

            <div className="relative mt-6 flex items-center gap-3 sm:mt-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-md bg-black/25 sm:h-14 sm:w-14">
                <BookOpen className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div>
                <div className="font-serif-display text-[18px] font-semibold sm:text-[20px]">1,200+ Authors</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/85">Shipped</div>
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="bg-brand-panel p-6 sm:p-8">
            {submitted ? (
              <div className="flex h-full min-h-[360px] flex-col items-center justify-center text-center sm:min-h-[440px]">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-brand text-white">
                  <Check className="h-7 w-7" />
                </div>
                <div className="mt-6 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                  Brief received
                </div>
                <h3 className="mt-2 font-serif-display text-[26px] font-medium leading-tight sm:text-[30px]">
                  Thank you, we've got it.
                </h3>
                <p className="mt-3 max-w-[320px] text-[13.5px] text-brand-ivory/65">
                  An editor will follow up within 24 hours with a customised plan. No calls unless you ask for one.
                </p>
                <button
                  onClick={close}
                  data-testid="consult-modal-close-success"
                  className="mt-8 min-h-[44px] rounded-md border border-brand-hair px-6 py-3 font-mono text-[11px] uppercase tracking-[0.28em] text-brand-ivory hover:border-brand hover:text-brand"
                >
                  Close
                </button>
              </div>
            ) : (
            <>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
              Consult
            </div>
            <h3 className="mt-1 font-serif-display text-[24px] font-semibold sm:text-[28px]">
              Get a customised plan
            </h3>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <FormField
                required
                value={form.name}
                onChange={(v) => update("name", v)}
                placeholder="Your name *"
              />
              <FormField
                required
                type="email"
                value={form.email}
                onChange={(v) => update("email", v)}
                placeholder="Your email *"
              />
              <FormField
                required
                type="tel"
                value={form.phone}
                onChange={(v) => update("phone", v)}
                placeholder="Your mobile number *"
              />
              <div className="relative">
                <select
                  value={form.goal}
                  onChange={(e) => update("goal", e.target.value)}
                  className="w-full appearance-none rounded-md border border-brand-hair bg-brand-panel2 px-4 py-3 text-[13.5px] text-brand-ivory focus:border-brand"
                >
                  <option value="">What are you looking for?</option>
                  {OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
                <ArrowRight className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 rotate-90 text-brand-mute" />
              </div>

              <div className="relative">
                <textarea
                  value={form.brief}
                  onChange={(e) => update("brief", e.target.value.slice(0, 500))}
                  placeholder="Tell us about your book / requirement *"
                  rows={4}
                  className="w-full resize-none rounded-md border border-brand-hair bg-brand-panel2 px-4 py-3 text-[13.5px] text-brand-ivory placeholder:text-brand-mute focus:border-brand"
                />
                <div className="absolute bottom-2 right-3 font-mono text-[10px] text-brand-mute">
                  {form.brief.length} / 500
                </div>
              </div>

              <label className="flex items-start gap-2 text-[12px] text-brand-ivory/70">
                <input
                  type="checkbox"
                  checked={form.agree}
                  onChange={(e) => update("agree", e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[#ff4e15]"
                />
                <span>
                  I agree to AuthorOS’s{" "}
                  <a href="#privacy" className="text-brand hover:underline">privacy policy</a> and{" "}
                  <a href="#terms" className="text-brand hover:underline">terms of service</a>.
                </span>
              </label>

              <button
                type="submit"
                className="group mt-1 flex w-full items-center justify-center gap-3 rounded-md bg-brand py-3 font-mono text-[12px] font-semibold uppercase tracking-[0.28em] text-white transition-colors hover:bg-[#ff5c26]"
              >
                Get my plan
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
            </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FormField({ value, onChange, placeholder, type = "text", required = false }) {
  return (
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-md border border-brand-hair bg-brand-panel2 px-4 py-3 text-[13.5px] text-brand-ivory placeholder:text-brand-mute focus:border-brand"
    />
  );
}
